import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, TextField, Typography,
} from '@mui/material';
import { COPY, pageFormatLabel, pageTypeCountText } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';
import { PresentationTemplate } from '../types';
import { focusVisibleSx } from '../ui/focus';
import StaticSlideView from './StaticSlideView';
import { CARD_GAP, CARD_WIDTH, columnsForWidth } from './assetBrowserLayout';
import {
  filterLayouts,
  filterTemplates,
  layoutPreviewSlide,
  layoutsForNewPage,
  previewLayout,
} from './templateLayoutPreview';

interface TemplatePickerPanelProps {
  templates: PresentationTemplate[];
  pageFormat?: string | null;
  panelWidth: number;
  busy?: boolean;
  disabled?: boolean;
  canManage?: boolean;
  onAddPage: (layoutId: string, templateId: string) => void;
  onEdit?: (templateId: string, layoutId?: string) => void;
  onDuplicate?: (templateId: string) => Promise<void>;
  onRename?: (templateId: string, name: string) => Promise<void>;
  onArchive?: (templateId: string) => Promise<void>;
  onCreate?: (name: string) => Promise<void>;
  onDuplicateLayout?: (templateId: string, layoutId: string) => Promise<void>;
  onCopyLayoutToKit?: (templateId: string, layoutId: string) => void;
  onMoveLayoutToKit?: (templateId: string, layoutId: string) => void;
}

const TemplatePickerPanel: React.FC<TemplatePickerPanelProps> = ({
  templates,
  pageFormat,
  panelWidth,
  busy,
  disabled,
  canManage,
  onAddPage,
  onEdit,
  onDuplicate,
  onRename,
  onArchive,
  onCreate,
  onDuplicateLayout,
  onCopyLayoutToKit,
  onMoveLayoutToKit,
}) => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [menu, setMenu] = useState<
    | { kind: 'kit'; el: HTMLElement; templateId: string }
    | { kind: 'layout'; el: HTMLElement; layoutId: string }
    | null
  >(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const columns = columnsForWidth(panelWidth);
  const locked = Boolean(disabled || busy || actionBusy);
  const selected = templates.find((template) => template._id === selectedId) || null;
  const filteredTemplates = useMemo(() => filterTemplates(templates, selected ? '' : query), [query, selected, templates]);
  const pageTypes = useMemo(() => (
    selected
      ? filterLayouts(canManage ? selected.layouts : layoutsForNewPage(selected.layouts), query)
      : []
  ), [canManage, query, selected]);
  const showLayoutManage = Boolean(canManage && (onDuplicateLayout || onCopyLayoutToKit || onMoveLayoutToKit));

  useEffect(() => {
    if (selectedId && !templates.some((template) => template._id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, templates]);

  const showMallList = !selected;
  const showManage = Boolean(canManage && (onEdit || onDuplicate || onRename || onArchive || onCreate));

  const openSelected = (templateId: string | null) => {
    setSelectedId(templateId);
    setQuery('');
    setActionError(null);
  };

  const handleMallClick = (template: PresentationTemplate) => {
    if (locked) return;
    if (canManage) {
      openSelected(template._id);
      return;
    }
    const usable = layoutsForNewPage(template.layouts);
    if (usable.length === 1) {
      onAddPage(usable[0].layoutId, template._id);
      return;
    }
    if (usable.length > 1) openSelected(template._id);
  };

  const runAction = async (work: () => Promise<void>) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await work();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || COPY.saveError);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
        {selected ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => openSelected(null)}
              title={COPY.allTemplates}
              aria-label={COPY.allTemplates}
              sx={{ mt: -0.25, color: PRES.textPrimary, ...focusVisibleSx }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
                {selected.name}
              </Typography>
              <Typography sx={{ mt: 0.5, fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary }}>
                {COPY.pageTypeHelp}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
              {COPY.templates}
            </Typography>
            <Typography sx={{ mt: 0.5, fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary }}>
              {COPY.templatePickerHelp}
            </Typography>
          </>
        )}
      </Box>
      <TextField
        size="small"
        placeholder={selected ? COPY.searchLayouts : COPY.searchTemplates}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mx: 1.5, my: 1 }}
      />
      {showMallList && showManage && onCreate && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={actionBusy}
            onClick={() => { setCreateName(''); setCreateOpen(true); }}
            sx={{ textTransform: 'none', color: PRES.interactive }}
          >
            {COPY.newTemplate}
          </Button>
        </Box>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mx: 1.5, mb: 1 }}>{actionError}</Alert>
      )}
      <Box sx={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', px: 1.5, pb: 2 }}>
        {showMallList && !templates.length && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.templatesEmpty}
          </Typography>
        )}
        {showMallList && !!templates.length && !filteredTemplates.length && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.noTemplateMatches}
          </Typography>
        )}
        {showMallList && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, ${CARD_WIDTH}px)`,
            gap: `${CARD_GAP}px`,
            justifyContent: 'start',
          }}>
            {filteredTemplates.map((template) => {
              const preview = previewLayout(template.layouts);
              const usable = layoutsForNewPage(template.layouts);
              return (
                <Box
                  key={template._id}
                  component="button"
                  type="button"
                  disabled={locked || !usable.length}
                  onClick={() => handleMallClick(template)}
                  sx={{
                    width: CARD_WIDTH,
                    p: 0,
                    m: 0,
                    textAlign: 'left',
                    border: `1px solid ${PRES.borderSubtle}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: locked || !usable.length ? 'default' : 'pointer',
                    bgcolor: PRES.surface,
                    color: 'inherit',
                    font: 'inherit',
                    opacity: locked || !usable.length ? 0.64 : 1,
                    position: 'relative',
                    '&:hover:not(:disabled)': { borderColor: PRES.interactive },
                    '&:hover:not(:disabled) .layout-add, &:focus-within .layout-add': { opacity: 1 },
                    '&:hover:not(:disabled) .template-menu, &:focus-within .template-menu': { opacity: 1 },
                    ...focusVisibleSx,
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <StaticSlideView
                      pageFormat={template.pageFormat || pageFormat}
                      slide={preview
                        ? layoutPreviewSlide(template._id, preview)
                        : layoutPreviewSlide(template._id, { layoutId: 'empty', objects: [] })}
                    />
                    {usable.length === 1 && (
                      <IconButton
                        className="layout-add"
                        size="small"
                        disabled={locked}
                        title={COPY.addSlide}
                        aria-label={COPY.addSlide}
                        onClick={(event) => {
                          event.stopPropagation();
                          onAddPage(usable[0].layoutId, template._id);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: showManage ? 38 : 6,
                          opacity: 0,
                          bgcolor: PRES.interactive,
                          color: PRES.white,
                          width: 28,
                          height: 28,
                          '&:hover': { bgcolor: PRES.interactiveHover },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                    {showManage && (
                      <IconButton
                        className="template-menu"
                        size="small"
                        disabled={actionBusy}
                        title={COPY.templateCardMenu}
                        aria-label={COPY.templateCardMenu}
                        onClick={(event) => {
                          event.stopPropagation();
                          setMenu({ kind: 'kit', el: event.currentTarget, templateId: template._id });
                        }}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          opacity: 0,
                          bgcolor: PRES.white,
                          color: PRES.textPrimary,
                          width: 28,
                          height: 28,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.16)',
                          '&:hover': { bgcolor: PRES.surfaceSunken },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 32,
                    px: 0.75,
                    pt: 0.75,
                    fontFamily: TYPE.ui,
                    ...TYPE.caption,
                    fontWeight: 650,
                    color: PRES.textPrimary,
                  }}>
                    {template.name}
                  </Typography>
                  <Typography noWrap sx={{
                    display: 'block',
                    px: 0.75,
                    pb: 0.75,
                    minHeight: 18,
                    fontFamily: TYPE.ui,
                    ...TYPE.caption,
                    color: PRES.textTertiary,
                  }}>
                    {pageFormatLabel(template.pageFormat)} · {pageTypeCountText(usable.length)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        {selected && !pageTypes.length && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.noLayoutMatches}
          </Typography>
        )}
        {selected && !!pageTypes.length && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, ${CARD_WIDTH}px)`,
            gap: `${CARD_GAP}px`,
            justifyContent: 'start',
          }}>
            {pageTypes.map((layout) => (
              <Box
                key={layout.layoutId}
                component="button"
                type="button"
                disabled={locked}
                onClick={() => onAddPage(layout.layoutId, selected._id)}
                sx={{
                  width: CARD_WIDTH,
                  p: 0,
                  m: 0,
                  textAlign: 'left',
                  border: `1px solid ${PRES.borderSubtle}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: locked ? 'default' : 'pointer',
                  bgcolor: PRES.surface,
                  color: 'inherit',
                  font: 'inherit',
                  opacity: locked ? 0.64 : 1,
                  position: 'relative',
                  '&:hover:not(:disabled)': { borderColor: PRES.interactive },
                  '&:hover:not(:disabled) .layout-add, &:focus-within .layout-add': { opacity: 1 },
                  '&:hover:not(:disabled) .layout-menu, &:focus-within .layout-menu': { opacity: 1 },
                  ...focusVisibleSx,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <StaticSlideView pageFormat={selected.pageFormat || pageFormat} slide={layoutPreviewSlide(selected._id, layout)} />
                  <IconButton
                    className="layout-add"
                    size="small"
                    disabled={locked}
                    title={COPY.addSlide}
                    aria-label={COPY.addSlide}
                    onClick={(event) => {
                      event.stopPropagation();
                      onAddPage(layout.layoutId, selected._id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: showLayoutManage ? 38 : 6,
                      opacity: 0,
                      bgcolor: PRES.interactive,
                      color: PRES.white,
                      width: 28,
                      height: 28,
                      '&:hover': { bgcolor: PRES.interactiveHover },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  {showLayoutManage && (
                    <IconButton
                      className="layout-menu"
                      size="small"
                      disabled={actionBusy}
                      title={COPY.layoutCardMenu}
                      aria-label={COPY.layoutCardMenu}
                      onClick={(event) => {
                        event.stopPropagation();
                        setMenu({ kind: 'layout', el: event.currentTarget, layoutId: layout.layoutId });
                      }}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        opacity: 0,
                        bgcolor: PRES.white,
                        color: PRES.textPrimary,
                        width: 28,
                        height: 28,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.16)',
                        '&:hover': { bgcolor: PRES.surfaceSunken },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
                <Typography sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  minHeight: 32,
                  px: 0.75,
                  py: 0.75,
                  fontFamily: TYPE.ui,
                  ...TYPE.caption,
                  fontWeight: 650,
                  color: PRES.textPrimary,
                }}>
                  {layout.name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={menu?.kind === 'kit' ? menu.el : undefined}
        open={menu?.kind === 'kit'}
        onClose={() => setMenu(null)}
      >
        {onEdit && (
          <MenuItem onClick={() => {
            const template = templates.find((item) => item._id === (menu?.kind === 'kit' ? menu.templateId : ''));
            setMenu(null);
            if (!template) return;
            onEdit(template._id, previewLayout(template.layouts)?.layoutId);
          }}>
            {COPY.editTemplate}
          </MenuItem>
        )}
        {onRename && (
          <MenuItem onClick={() => {
            const template = templates.find((item) => item._id === (menu?.kind === 'kit' ? menu.templateId : ''));
            setMenu(null);
            if (!template) return;
            setRenameId(template._id);
            setRenameValue(template.name);
          }}>
            {COPY.renameTemplate}
          </MenuItem>
        )}
        {onDuplicate && (
          <MenuItem onClick={() => {
            const templateId = menu?.kind === 'kit' ? menu.templateId : undefined;
            setMenu(null);
            if (templateId) void runAction(() => onDuplicate(templateId));
          }}>
            {COPY.duplicateTemplate}
          </MenuItem>
        )}
        {onArchive && (
          <MenuItem onClick={() => {
            const templateId = menu?.kind === 'kit' ? menu.templateId : undefined;
            setMenu(null);
            if (!templateId) return;
            if (!window.confirm(COPY.confirmArchiveTemplate)) return;
            void runAction(() => onArchive(templateId));
          }}>
            {COPY.deleteTemplate}
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={menu?.kind === 'layout' ? menu.el : undefined}
        open={menu?.kind === 'layout'}
        onClose={() => setMenu(null)}
      >
        {onDuplicateLayout && selected && (
          <MenuItem onClick={() => {
            const layoutId = menu?.kind === 'layout' ? menu.layoutId : undefined;
            const templateId = selected._id;
            setMenu(null);
            if (layoutId) void runAction(() => onDuplicateLayout(templateId, layoutId));
          }}>
            {COPY.duplicateLayout}
          </MenuItem>
        )}
        {onCopyLayoutToKit && selected && (
          <MenuItem onClick={() => {
            const layoutId = menu?.kind === 'layout' ? menu.layoutId : undefined;
            setMenu(null);
            if (layoutId) onCopyLayoutToKit(selected._id, layoutId);
          }}>
            {COPY.copyLayoutToKit}
          </MenuItem>
        )}
        {onMoveLayoutToKit && selected && (
          <MenuItem
            disabled={selected.layouts.length <= 1}
            onClick={() => {
              const layoutId = menu?.kind === 'layout' ? menu.layoutId : undefined;
              setMenu(null);
              if (layoutId) onMoveLayoutToKit(selected._id, layoutId);
            }}
          >
            {COPY.moveLayoutToKit}
          </MenuItem>
        )}
      </Menu>
      <Dialog open={Boolean(renameId)} onClose={() => setRenameId(null)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.renameTemplate}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.templateName}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameId(null)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!renameValue.trim() || actionBusy || !renameId || !onRename}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={() => {
              if (!renameId || !onRename) return;
              void runAction(async () => {
                await onRename(renameId, renameValue.trim());
                setRenameId(null);
              });
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.newTemplate}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.templateName}
            placeholder={COPY.templateNamePlaceholder}
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!createName.trim() || actionBusy || !onCreate}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={() => {
              if (!onCreate) return;
              void runAction(async () => {
                await onCreate(createName.trim());
                setCreateOpen(false);
              });
            }}
          >
            {COPY.createAction}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatePickerPanel;
